import React from 'react';
import DocumentPage, {
  Section,
  SubSection,
  Paragraph,
  BulletList,
  DoDoNot,
} from './DocumentPage';

const FireGeneralSafetyDoc = ({ onReturnHome }) => (
  <DocumentPage
    pageTitle="General Fire Safety – Safe System of Work"
    documentHeading="Safe System of Work: General Fire Safety"
    meta={[
      ['Document Reference', 'PVS-SSOW-FIRE'],
      ['Version', '1.0'],
      ['Date of Issue', '17th March 2026'],
      ['Next Review Date', '17th March 2028'],
      ['Activity', 'General fire safety and evacuation awareness'],
      ['Review Period', '24 months'],
    ]}
    acknowledgment={{
      documentName: 'General Fire Safety – Safe System of Work',
      documentReference: 'PVS-SSOW-FIRE',
      processActivity: 'General Fire Safety',
      versionNumber: '1.0',
      dateOfIssue: '17th March 2026',
    }}
    onReturnHome={onReturnHome}
  >
    <Section number="1" title="Introduction">
      <Paragraph>
        All fires, however small, are a threat to yourself, your colleagues, visitors and the
        organisation. Most fires can be prevented by taking simple precautions, however fires can
        still occur, and it is important that you know what to do in the event of a fire.
      </Paragraph>
    </Section>

    <Section number="2" title="Safe System of Work">
      <DoDoNot
        doItems={[
          'Ensure you are aware of the fire evacuation procedure for the premises, including: the nearest available safe exit and any alternatives; the means of raising the alarm; position of fire extinguishers and the types of fire they can be used for; location of the fire assembly point.',
          'Ensure you know where fire safety equipment is located e.g. fire extinguishers, fire blankets and other firefighting equipment, alarm call points, fire safety notices and instructions.',
          'Ensure fire routes are kept clear at all times.',
          'Raise any concerns immediately to your supervisor or line manager.',
          'Report anyone acting suspiciously and ensure fire exits are kept fully closed when not in use.',
          'If you discover a fire: alert others e.g. by shouting FIRE! FIRE! or by triggering an alarm call point (red box); evacuate the building by the nearest safe exit; only use a fire extinguisher or other firefighting equipment if you are trained and confident to do so, but NEVER put yourself in danger. If you choose to tackle a fire, make sure you have a safe means of escape.',
          'Only smoke in designated areas and ensure matches, cigarettes etc are put out properly.',
          'Keep wheelie bins secure and closed out of hours to help prevent arson.',
        ]}
        doNotItems={[
          'Obstruct any fire points, fire doors, escape routes or fire signs.',
          'Misuse or tamper with any equipment provided for fire safety e.g. do not use fire extinguishers to wedge open doors or discharge fire extinguishers during horseplay.',
          'Overload electrical sockets.',
          'Use lifts in the event of a fire unless they are specifically installed "firefighting" lifts.',
          'Wedge open fire doors – they are there to prevent the passage of heat and smoke.',
          'Store combustible materials next to sources of ignition.',
          'Allow rubbish to accumulate or to be left outside where someone could set fire to it.',
          'Go back into a building until permitted to do so by the fire officer / incident controller.',
          'Smoke inside the building or anywhere not designated as a smoking area.',
        ]}
      />
    </Section>

    <Section number="3" title="Know Your Fire Extinguisher">
      <Paragraph className="mb-4">
        Not all types of fire extinguisher can be used on all types of fire. There are five main
        types of fire extinguisher. The type is usually identified by a coloured band or label.
      </Paragraph>

      <SubSection title="Water Extinguisher (Red band / panel)">
        <BulletList
          items={[
            'Can be used on combustible organic materials such as wood, paper, cardboard, fabrics etc.',
            'Do not use on live electrical equipment, kitchen fires or flammable liquids.',
            'Water extinguishers are usually heavy.',
          ]}
        />
      </SubSection>

      <SubSection title="Carbon Dioxide (CO₂) Extinguisher (Black band / panel)">
        <BulletList
          items={[
            'Mainly used on electrical fires, can also be used for flammable liquids.',
            'Works by displacing the oxygen the fire needs to burn.',
            'Do not hold the horn when using this type of extinguisher as this will get very cold – hold the extinguisher body instead.',
          ]}
        />
      </SubSection>

      <SubSection title="Dry Powder Extinguisher (Blue band / panel)">
        <BulletList
          items={[
            'Can be used on most types of fire e.g. organic materials, flammable liquids, gasses and metals, electrical fires.',
            'Do not use on fires involving cooking oils and fats.',
            'Not recommended for use in confined spaces as the powder can be inhaled.',
          ]}
        />
      </SubSection>

      <SubSection title="Foam Extinguisher (Cream band / panel)">
        <BulletList
          items={[
            'Can be used on combustible organic material and liquid fires.',
            'Do not use on live electrical equipment or kitchen fires.',
          ]}
        />
      </SubSection>

      <SubSection title="Wet Chemical Extinguisher (Yellow band / panel)">
        <BulletList
          items={[
            'Mainly used on cooking oils and fats, can also be used on combustible organic materials.',
            'Do not use on live electrical equipment or flammable liquids.',
          ]}
        />
      </SubSection>
    </Section>

    <Section number="4" title="Using a Fire Extinguisher – Remember PASS">
      <BulletList
        items={[
          'Pull out the pin – this will allow the extinguisher to discharge.',
          'Aim at the base of the fire – if you aim at the flames the extinguishing agent will fly right through and be ineffective.',
          'Squeeze the top handle or lever – to release the pressurised extinguishing agent.',
          'Sweep from side to side until the fire is completely out. Start using the extinguisher from a safe distance away, and then move forward. Once the fire is out, keep an eye on the area in case the fire re-ignites.',
        ]}
      />
      <Paragraph className="mt-4">
        There may be other types of firefighting equipment e.g. fire blankets, hoses, sprinklers etc
        in your premises. You should make sure that you know how to use any other relevant
        equipment.
      </Paragraph>
    </Section>

    <Section number="5" title="Reminder">
      <Paragraph className="font-semibold">
        You have a duty not only to protect your own health and safety but also to ensure that your
        actions do not put others around you at risk.
      </Paragraph>
    </Section>
  </DocumentPage>
);

export default FireGeneralSafetyDoc;
